/**
 * Zellauswahl des Grundkalenders.
 *
 * Die Auswahl haengt immer an genau einer Apartmentzeile; mehrere getrennte Zeitraeume sind
 * erlaubt. Ziehen waehlt einen Zeitraum, Strg/Cmd legt einen weiteren daneben, Shift verlaengert
 * den zuletzt gewaehlten. Der Wechsel auf ein anderes Apartment liegt bewusst nicht hier,
 * sondern bei der Ansicht — nur sie kann vorher bestaetigen lassen.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MAX_RANGES,
  applyDraft,
  normalizeRange,
  type SelectedRange,
  type SelectionMode,
} from './rangeSelectionModel';

interface DraftState {
  anchor: string;
  cursor: string;
  mode: SelectionMode;
}

let rangeCounter = 0;

function nextRangeId(): string {
  rangeCounter += 1;
  return `range-${rangeCounter}`;
}

export function useRangeSelection() {
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const [ranges, setRanges] = useState<SelectedRange[]>([]);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const draftRef = useRef<DraftState | null>(null);
  const dragging = useRef(false);

  const putDraft = useCallback((next: DraftState | null) => {
    draftRef.current = next;
    setDraft(next);
  }, []);

  const commit = useCallback(() => {
    dragging.current = false;
    const pending = draftRef.current;
    if (!pending) return;
    putDraft(null);
    const next = normalizeRange(pending.anchor, pending.cursor);
    setRanges((existing) => applyDraft(existing, next, pending.mode, nextRangeId()));
  }, [putDraft]);

  useEffect(() => {
    window.addEventListener('pointerup', commit);
    window.addEventListener('pointercancel', commit);
    return () => {
      window.removeEventListener('pointerup', commit);
      window.removeEventListener('pointercancel', commit);
    };
  }, [commit]);

  /**
   * Beginnt eine Auswahl. Per Tastatur ist der Druck Beginn und Ende zugleich (`immediate`): es
   * gibt kein Loslassen, das sie übernähme, und ein offener Entwurf folgte sonst dem Mauszeiger.
   */
  const begin = useCallback(
    (nextPropertyId: string, date: string, mode: SelectionMode, immediate = false) => {
      dragging.current = true;
      const sameRow = propertyId === nextPropertyId;
      const carried = sameRow ? ranges : [];
      if (!sameRow) {
        setPropertyId(nextPropertyId);
        setRanges([]);
      }
      const effective = carried.length === 0 && mode !== 'replace' ? 'replace' : mode;
      const anchor = effective === 'extend' ? (carried[carried.length - 1]?.from ?? date) : date;
      putDraft({ anchor, cursor: date, mode: effective });
      if (immediate) commit();
    },
    [commit, propertyId, putDraft, ranges],
  );

  const extend = useCallback(
    (date: string) => {
      if (!dragging.current || !draftRef.current) return;
      putDraft({ ...draftRef.current, cursor: date });
    },
    [putDraft],
  );

  /** Setzt die Auswahl von aussen — etwa beim Sprung aus der Kanalsteuerung. */
  const select = useCallback(
    (nextPropertyId: string, from: string, to: string) => {
      dragging.current = false;
      putDraft(null);
      setPropertyId(nextPropertyId);
      setRanges([{ id: nextRangeId(), ...normalizeRange(from, to) }]);
    },
    [putDraft],
  );

  const clear = useCallback(() => {
    dragging.current = false;
    putDraft(null);
    setRanges([]);
    setPropertyId(null);
  }, [putDraft]);

  const addRange = useCallback((from: string, to: string) => {
    setRanges((existing) =>
      existing.length >= MAX_RANGES ? existing : [...existing, { id: nextRangeId(), from, to }],
    );
  }, []);

  const removeRange = useCallback((id: string) => {
    setRanges((existing) => existing.filter((range) => range.id !== id));
  }, []);

  const patchRange = useCallback((id: string, patch: Partial<Omit<SelectedRange, 'id'>>) => {
    setRanges((existing) =>
      existing.map((range) => (range.id === id ? { ...range, ...patch } : range)),
    );
  }, []);

  const preview = draft ? normalizeRange(draft.anchor, draft.cursor) : null;
  const visible =
    preview && draft ? applyDraft(ranges, preview, draft.mode, 'range-draft') : ranges;

  return {
    propertyId,
    ranges: visible,
    dragging: draft !== null,
    begin,
    extend,
    commit,
    select,
    clear,
    addRange,
    removeRange,
    patchRange,
  };
}
